import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { derivePillState, NasHealth, ProbeResult } from './nasStatusLogic';

const COLORS: Record<string, string> = {
  online: '#4caf50', line_down: '#ff9800', down: '#f44336', checking: '#9e9e9e',
};
const LABELS: Record<string, string> = {
  online: 'NAS Online', line_down: 'Line Down', down: 'NAS Down', checking: 'Checking…',
};

function fmtAge(iso: string | null): string {
  if (!iso) return 'never';
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (Number.isNaN(mins)) return 'unknown';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

function NasStatusPill() {
  const [probe, setProbe] = useState<ProbeResult>('checking');
  const [health, setHealth] = useState<NasHealth | null>(null);
  const [hover, setHover] = useState(false);
  const [tipPos, setTipPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);

  // The pill lives inside the fixed sidebar (overflow-y: auto ⇒ overflow-x
  // clips too), so the tooltip must be viewport-positioned to escape the
  // sidebar's 240px clip box. Clamp so it never runs off the right edge.
  const TIP_WIDTH = 320;
  const showTip = () => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      setTipPos({
        top: rect.bottom + 6,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - TIP_WIDTH - 8)),
      });
    }
    setHover(true);
  };

  // Fast axis: the tab's own reachability probe every 60s. Same-origin,
  // readable (NOT no-cors — CloudFront 5xx must count as down, 7/3 rule).
  useEffect(() => {
    const check = () => {
      fetch(`${window.location.origin}/stream/health-check`)
        .then(r => setProbe(r.ok ? 'ok' : 'fail'))
        .catch(() => setProbe('fail'));
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, []);

  // Slow axis: heartbeat record every 3 min + on tab focus.
  useEffect(() => {
    const load = () => { api.getNasStatus().then(setHealth).catch(() => setHealth(null)); };
    load();
    const t = setInterval(load, 180000);
    window.addEventListener('focus', load);
    return () => { clearInterval(t); window.removeEventListener('focus', load); };
  }, []);

  const { state, confirming } = derivePillState(probe, health, new Date());

  const tipLines: string[] = state === 'online'
    ? ['All paths healthy — streaming, uploads, and backups running.',
       `NAS heartbeat: ${fmtAge(health?.heartbeatLastSeen ?? null)}`]
    : [
        `Internet → NAS: unreachable${health?.lastStatusChange ? ` — since ${new Date(health.lastStatusChange).toLocaleString()}` : ''}`,
        confirming
          ? 'NAS heartbeat: confirming…'
          : `NAS heartbeat: ${state === 'line_down' ? '✓' : '✗'} ${fmtAge(health?.heartbeatLastSeen ?? null)}`,
        'Affected: streaming, downloads via NAS, vendor SFTP deliveries.',
        ...(state === 'line_down'
          ? ['Still running: archiving, S3 backup, database sync.']
          : ['No evidence the NAS is alive — check power/hardware.']),
        ...(health?.lastChecked ? [`Last AWS check: ${new Date(health.lastChecked).toLocaleTimeString()}`] : []),
      ];

  return (
    <span ref={anchorRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          onMouseEnter={showTip} onMouseLeave={() => setHover(false)}>
      <span style={{
        width: 10, height: 10, borderRadius: '50%',
        background: COLORS[state],
        boxShadow: state !== 'checking' ? `0 0 6px ${COLORS[state]}` : 'none',
      }} />
      <span style={{ fontSize: 13, color: COLORS[state], fontWeight: 600 }}>{LABELS[state]}</span>
      {hover && tipPos && (
        <div style={{
          position: 'fixed', top: tipPos.top, left: tipPos.left, zIndex: 1000, width: TIP_WIDTH,
          background: '#fff', border: '1px solid #ddd', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: '10px 14px',
          fontSize: 12, color: '#333', lineHeight: 1.6, textAlign: 'left',
        }}>
          {tipLines.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}
    </span>
  );
}

export default NasStatusPill;
